import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useInView } from 'react-intersection-observer';
import { MEDIA_BREAKPOINTS, SHORT_HEIGHT, ULTRA_NARROW } from '@/constants/layout';
import type { Fonts } from '@/theme/themes';

type SizeOption = 'xxsmall' | 'xsmall' | 'small' | 'medium' | 'large';

interface AnimatedDescriptionProps {
  $isAnimated: boolean;
  fontFamily?: keyof Fonts;
  $size: SizeOption;
  $preserve: boolean;
  $customFontSize?: string;
  $lineHeight?: number;
}

const fontSizeKey = (s: SizeOption) =>
  s === 'xxsmall'
    ? 'descriptionXxsmallFontSize'
    : s === 'xsmall'
    ? 'descriptionXsmallFontSize'
    : s === 'small'
    ? 'descriptionSmallFontSize'
    : s === 'medium'
    ? 'descriptionMediumFontSize'
    : 'descriptionLargeFontSize';

const AnimatedDescription = styled.p.attrs({ dir: 'rtl' })<AnimatedDescriptionProps>`
  font-family: ${({ theme, fontFamily }) =>
    fontFamily ? theme.fonts[fontFamily] : theme.fonts.rubikBoldItalic};
  line-height: ${({ $lineHeight }) => $lineHeight ?? 1.4};
  margin: 0;
  color: ${({ theme }) => theme.descriptionColor};

  text-shadow:
    -1px -3px 0 ${({ theme }) => theme.shadowDarkColor},
    1px -2px 0 ${({ theme }) => theme.shadowDarkColor},
    -1px 1px 0 ${({ theme }) => theme.shadowDarkColor},
    1px 1px 0 ${({ theme }) => theme.shadowDarkColor};

  white-space: ${({ $preserve }) => ($preserve ? 'pre-line' : 'normal')};

  transition: transform 0.5s ease, opacity 0.5s ease;
  opacity: ${({ $isAnimated }) => ($isAnimated ? 1 : 0)};
  transform: ${({ $isAnimated }) =>
    $isAnimated ? 'translateX(0)' : 'translateX(-20px)'};

  @media (prefers-reduced-motion: reduce) {
    transition: none;
    transform: none;
    opacity: 1;
  }

  overflow-wrap: anywhere;
  word-break: break-word;
  hyphens: auto;

  font-size: ${({ theme, $size, $customFontSize }) =>
    $customFontSize ?? theme.sizes.small[fontSizeKey($size)]};

  @media (max-width: ${MEDIA_BREAKPOINTS.smallScreen}) {
    font-size: ${({ theme, $customFontSize }) =>
      $customFontSize ?? theme.sizes.small[fontSizeKey('xsmall')]};
  }

  @media (min-width: ${MEDIA_BREAKPOINTS.mediumScreen}) {
    font-size: ${({ theme, $size, $customFontSize }) =>
      $customFontSize ?? theme.sizes.medium[fontSizeKey($size)]};
  }

  @media (min-width: ${MEDIA_BREAKPOINTS.largeScreen}) {
    font-size: ${({ theme, $size, $customFontSize }) =>
      $customFontSize ?? theme.sizes.large[fontSizeKey($size)]};
  }
    @media (min-width: ${MEDIA_BREAKPOINTS.ultraWide}) {
  font-size: ${({ theme, $size, $customFontSize }) =>
    $customFontSize ?? theme.sizes.ultra[fontSizeKey($size)]};
}

  @media (max-width: ${ULTRA_NARROW}), (max-height: ${SHORT_HEIGHT}) {
    font-size: ${({ theme, $customFontSize }) =>
      $customFontSize ?? theme.sizes.small[fontSizeKey('xxsmall')]};
    line-height: ${({ $lineHeight }) => ($lineHeight ? Math.min($lineHeight, 1.33) : 1.32)};
  }
`;

export interface DescriptionProps {
  children?: React.ReactNode;
  fontFamily?: keyof Fonts;
  size?: SizeOption;
  template?: string;
  keywords?: { service?: string; city?: string };
  as?: keyof JSX.IntrinsicElements;
  itemProp?: string;
  preserveLineBreaks?: boolean;
  customFontSize?: string;
  lineHeight?: number;
  initiallyVisible?: boolean;
}

const Description: React.FC<DescriptionProps> = ({
  children,
  fontFamily,
  size = 'medium',
  template,
  keywords,
  as = 'p',
  itemProp,
  preserveLineBreaks = true,
  customFontSize,
  lineHeight,
  initiallyVisible = false,
}) => {
  const [isAnimated, setIsAnimated] = useState(initiallyVisible);
  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.2 });

  useEffect(() => {
    if (initiallyVisible) {
      setIsAnimated(true);
      return;
    }

    setIsAnimated(inView);
  }, [initiallyVisible, inView]);

  const textFromTemplate = useMemo(() => {
    if (!template) return null;
    return template
      .replace('{service}', keywords?.service ?? '')
      .replace('{city}', keywords?.city ?? '');
  }, [template, keywords?.service, keywords?.city]);

  return (
    <AnimatedDescription
      as={as}
      ref={ref}
      $isAnimated={isAnimated}
      fontFamily={fontFamily}
      $size={size}
      $preserve={preserveLineBreaks}
      itemProp={itemProp}
      $customFontSize={customFontSize}
      $lineHeight={lineHeight}
    >
      {textFromTemplate ?? children}
    </AnimatedDescription>
  );
};

export default Description;
